// IndexedDB wrapper simple pour la file d'attente offline
// Supporte plusieurs stores (timesheets, absences, frais, notes)
// Les records sont filtrés par user_id : une saisie d'un utilisateur ne doit jamais être
// envoyée sous l'identité d'un autre.
const DB_NAME = "oscop_offline";
const DB_VERSION = 3;
const STORES = ["pending_timesheets", "pending_absences", "pending_frais", "pending_notes"];
// Mapping store -> endpoint API pour synchronisation
export const STORE_ENDPOINTS = {
  pending_timesheets: "/timesheets",
  pending_absences: "/absences",
  pending_frais: "/frais",
  pending_notes: "/notes-terrain",
};
export const STORE_LABELS = {
  pending_timesheets: "feuille de temps",
  pending_absences: "absence",
  pending_frais: "frais",
  pending_notes: "note terrain",
};

function uuid() {
  // Fallback RFC4122 v4 si crypto.randomUUID absent (anciens Safari ou contextes non-sécurisés)
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function currentUserId() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    const u = JSON.parse(raw);
    return u?.id || null;
  } catch {
    return null;
  }
}

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      for (const name of STORES) {
        if (!db.objectStoreNames.contains(name)) {
          const store = db.createObjectStore(name, { keyPath: "local_id" });
          store.createIndex("created_at", "created_at", { unique: false });
          store.createIndex("user_id", "user_id", { unique: false });
        } else {
          try {
            const tx = e.target.transaction;
            const store = tx.objectStore(name);
            if (!store.indexNames.contains("user_id")) {
              store.createIndex("user_id", "user_id", { unique: false });
            }
          } catch {}
        }
      }
    };
  });
}

async function withStore(storeName, mode, callback) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    const result = callback(store);
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
  });
}

/** Enqueue a payload for a given store. */
export async function queueItem(storeName, payload) {
  if (!STORES.includes(storeName)) throw new Error(`Unknown store: ${storeName}`);
  const user_id = currentUserId();
  const local_id = `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  // Pour les feuilles de temps on garantit un client_uuid stable (idempotence serveur)
  const enriched = { ...payload };
  if (storeName === "pending_timesheets" && !enriched.client_uuid) {
    enriched.client_uuid = uuid();
  }
  const record = {
    local_id,
    user_id,
    payload: enriched,
    created_at: new Date().toISOString(),
    attempts: 0,
    last_error: null,
  };
  await withStore(storeName, "readwrite", (store) => store.add(record));
  try { window.dispatchEvent(new CustomEvent("oscop-offline-queue-changed")); } catch {}
  return record;
}

// Backward compat
export const queueTimesheet = (payload) => queueItem("pending_timesheets", payload);

export async function listPending(storeName, { user_id } = {}) {
  return new Promise(async (resolve, reject) => {
    const db = await openDB();
    const tx = db.transaction(storeName, "readonly");
    const store = tx.objectStore(storeName);
    const req = store.getAll();
    req.onsuccess = () => {
      let items = req.result || [];
      if (user_id) items = items.filter((r) => r.user_id === user_id);
      resolve(items);
    };
    req.onerror = () => reject(req.error);
  });
}

export async function listAllPending({ user_id } = {}) {
  const result = {};
  const uid = user_id !== undefined ? user_id : currentUserId();
  for (const name of STORES) {
    result[name] = uid ? await listPending(name, { user_id: uid }) : await listPending(name);
  }
  return result;
}

export async function removePending(storeName, local_id) {
  await withStore(storeName, "readwrite", (store) => store.delete(local_id));
}

export async function markAttempt(storeName, local_id, error) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    const getReq = store.get(local_id);
    getReq.onsuccess = () => {
      const rec = getReq.result;
      if (!rec) return resolve(null);
      rec.attempts = (rec.attempts || 0) + 1;
      rec.last_error = error ? String(error).slice(0, 300) : null;
      store.put(rec);
    };
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
}

export async function countPendingAll({ user_id } = {}) {
  const all = await listAllPending({ user_id });
  return Object.values(all).reduce((n, arr) => n + arr.length, 0);
}

/** Nouveau client_uuid stable (helper exposé). */
export function newClientUuid() {
  return uuid();
}
